import React from "react";
import { MDBCard, MDBCardBody, MDBIcon, MDBTooltip } from "mdb-react-ui-kit";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Button, Tag } from "./ui";
import { useTranslation } from "react-i18next";

const EDIT_PEN_ICON = (
  <svg
    width="10"
    height="10"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M5 1.56066H1.5C1.23478 1.56066 0.98043 1.66602 0.792893 1.85355C0.605357 2.04109 0.5 2.29544 0.5 2.56066V9.56066C0.5 9.82588 0.605357 10.0802 0.792893 10.2678C0.98043 10.4553 1.23478 10.5607 1.5 10.5607H8.5C8.76522 10.5607 9.01957 10.4553 9.20711 10.2678C9.39464 10.0802 9.5 9.82588 9.5 9.56066V6.06066M8.75 0.81066C8.94891 0.611748 9.2187 0.5 9.5 0.5C9.7813 0.5 10.0511 0.611748 10.25 0.81066C10.4489 1.00957 10.5607 1.27936 10.5607 1.56066C10.5607 1.84196 10.4489 2.11175 10.25 2.31066L5.5 7.06066L3.5 7.56066L4 5.56066L8.75 0.81066Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AvailabilityScheduleCard = ({
  availableHours,
  onEditClick,
  isEditDisabled = false,
  showEditButton = false,
}) => {
  const { t } = useTranslation();

  const daysOrder = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const editButton = (
    <Button
      type="button"
      outline
      color="info"
      size="table"
      className="availability-edit-btn"
      disabled={isEditDisabled}
      onClick={isEditDisabled ? undefined : onEditClick}
      icon={EDIT_PEN_ICON}
    >
      {t("edit")}
    </Button>
  );

  return (
    <MDBCard
      className="availability-schedule-card border-0 shadow-sm rounded-4 h-100"
      style={{ backgroundColor: "#fff" }}
    >
      <MDBCardBody style={{ padding: "24px" }}>
        {/* Header — title left, Edit right (previous placement) */}
        <div className="availability-schedule-card__header">
          <h4 className="availability-schedule-card__title mb-0">
            {t("Availability Hours")}
          </h4>
          {showEditButton &&
            (isEditDisabled ? (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    {t("Availability hours should be managed in Foodics")}
                  </Tooltip>
                }
              >
                <span className="d-inline-flex">{editButton}</span>
              </OverlayTrigger>
            ) : (
              editButton
            ))}
        </div>

        {/* Sync Status - Styled to match Figma exactly */}
        <div
          className="d-flex justify-content-between align-items-center w-100 px-5"
          style={{ paddingBottom: "12px" }}
        >
          {/* Keeta */}
          <div className="d-flex align-items-start gap-1">
            <MDBIcon
              icon="check-circle"
              className="small mt-1"
              style={{ color: "#00D69F" }}
            />
            <span className="fw-medium text-dark small">
              {t("Keeta")}
            </span>
            <MDBTooltip
              tag="span"
              title={t(
                "Hours are updated immediately when the branch is saved"
              )}
            >
              <img
                src="/images/information-circle.svg"
                alt="info"
                width={10}
                height={10}
                style={{
                  verticalAlign: "super",
                }}
              />
            </MDBTooltip>
          </div>

          {/* Ninja */}
          <div className="d-flex align-items-start gap-1">
            <MDBIcon icon="sync" className="text-primary small mt-1" />
            <span className="fw-medium text-dark small">
              {t("Ninja")}
            </span>
            <MDBTooltip
              tag="span"
              title={t("Hours will update during the next catalog sync")}
            >
              <img
                src="/images/information-circle.svg"
                alt="info"
                width={10}
                height={10}
                style={{
                  verticalAlign: "super",
                }}
              />
            </MDBTooltip>
          </div>

          {/* Chefz & Jahez */}
          <div className="d-flex align-items-start gap-1">
            <MDBIcon
              icon="times-circle"
              className=" small mt-1"
              style={{ color: "#E84A4A" }}
            />
            <span className="fw-medium text-dark small">
              {t("Chefz and Jahez")}
            </span>
            <MDBTooltip
              tag="span"
              title={t("Availability hours not supported")}
            >
              <img
                src="/images/information-circle.svg"
                alt="info"
                width={10}
                height={10}
                style={{
                  verticalAlign: "super",
                }}
              />
            </MDBTooltip>
          </div>
        </div>

        {/* Schedule List */}
        <div className="availability-list">
          <style>{`
            .last-availability-item {
              padding-bottom: 4px !important;
            }
          `}</style>
          {!availableHours || Object.keys(availableHours).length === 0 ? (
            <div className="text-center text-muted py-5">
              {t("No Schedule Available")}
            </div>
          ) : (
            daysOrder.map((day, index, array) => {
              const details = availableHours[day.toLowerCase()];
              if (!details) return null;

              return (
                <div
                  key={day}
                  className={`d-flex justify-content-between align-items-center py-2 ${
                    index === array.length - 1 ? "last-availability-item" : ""
                  }`}
                  style={
                    index !== array.length - 1
                      ? { borderBottom: "1px solid #f0f0f0" }
                      : {}
                  }
                >
                  <span
                    className="text-dark fw-bold"
                    style={{ fontSize: "0.95rem" }}
                  >
                    {t(day)}
                  </span>
                  <div className="d-flex flex-wrap gap-2 justify-content-end">
                    {details.is_visible && details.times?.length > 0 ? (
                      details.times.map((time, i) => (
                        <span
                          key={i}
                          className="badge rounded-2 px-3 py-2"
                          style={{
                            backgroundColor: "#118AB2",
                            fontSize: "0.8rem",
                            fontWeight: "500",
                          }}
                        >
                          {time.start} - {time.end}
                        </span>
                      ))
                    ) : (
                      <Tag variant="error">{t("Closed")}</Tag>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </MDBCardBody>
    </MDBCard>
  );
};

export default AvailabilityScheduleCard;
